<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <OfflineIndicator />
    <router-view />
    <InstallPwaButton />
    <ReloadPrompt />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import { useUserStore } from './stores/user'
import { tankReconciliationService } from './services/tankReconciliationService'
import InstallPwaButton from './components/InstallPwaButton.vue'
import ReloadPrompt from './components/ReloadPrompt.vue'
import OfflineIndicator from './components/OfflineIndicator.vue'

let authStore: ReturnType<typeof useAuthStore> | null = null
let userStore: ReturnType<typeof useUserStore> | null = null
let apiRefreshInterval: NodeJS.Timeout | null = null

// Handle auth state changes from other tabs/windows
const handleStorageChange = (event: StorageEvent) => {
  if (event.key === 'supabase_session' || event.key === 'supabase_user') {
    authStore?.initFromLocalStorage()
  }
}

// Auto-refresh API data in the background
const startApiAutoRefresh = () => {
  // Clear any existing interval
  if (apiRefreshInterval) {
    clearInterval(apiRefreshInterval)
  }

  // Refresh immediately on login
  console.log('🔄 Starting automatic API refresh...')
  refreshApiData()

  // Then refresh every hour (3600000 ms)
  apiRefreshInterval = setInterval(() => {
    refreshApiData()
  }, 3600000) // 1 hour

  console.log('✅ Automatic API refresh enabled (every 1 hour)')
}

const stopApiAutoRefresh = () => {
  if (apiRefreshInterval) {
    clearInterval(apiRefreshInterval)
    apiRefreshInterval = null
    console.log('⏹️ Automatic API refresh stopped')
  }
}

const refreshApiData = async () => {
  try {
    console.log('🔄 [Auto-Refresh] Fetching latest tank API readings...')
    await tankReconciliationService.refreshTankApiReadings()
    console.log('✅ [Auto-Refresh] Tank API readings updated successfully')
    
    // Also run daily accountability checks
    console.log('🔍 [Auto-Check] Running daily accountability checks...')
    const { notificationService } = await import('./services/notificationService')
    await notificationService.runDailyChecks()
    console.log('✅ [Auto-Check] Daily checks completed')
  } catch (error) {
    console.error('❌ [Auto-Refresh] Failed to refresh API data:', error)
    // Don't show alert for background refresh failures - just log them
  }
}

// Initialize auth on app mount
onMounted(async () => {
  authStore = useAuthStore()
  userStore = useUserStore()

  // Initialize auth store
  if (!authStore.state.initialized) {
    await authStore.initAuth()
  }
  // Initialize user profile if authenticated
  if (authStore.isAuthenticated && !userStore.state.initialized) {
    await userStore.initProfile()
  }

  // Start auto-refresh if user is already logged in
  if (authStore.isAuthenticated) {
    startApiAutoRefresh()
  }

  // Watch for auth state changes
  watch(() => authStore?.isAuthenticated, (isAuthenticated) => {
    if (isAuthenticated) {
      console.log('👤 User logged in - starting API auto-refresh')
      startApiAutoRefresh()
    } else {
      console.log('👤 User logged out - stopping API auto-refresh')
      stopApiAutoRefresh()
    }
  })

  // Set up auth state change listener
  window.addEventListener('storage', handleStorageChange)
})

onUnmounted(() => {
  window.removeEventListener('storage', handleStorageChange)
  stopApiAutoRefresh()
})
    
</script>

<style>
@import './assets/main.css';

#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>